import React, { useState, useEffect, useRef } from 'react';
import { socketService } from '../utils/socket';
import { localStorageManager } from '../utils/localStorage';
import SocketNotification from '../components/SocketNotification';
import './TimerPage.css';

interface TimerCategory {
  id: string;
  name: string;
  minutes: number;
  seconds: number;
}

const TimerPage: React.FC = () => {
  const [config, setConfig] = useState({
    minutes: 2,
    seconds: 0,
    title: 'Tiempo Restante',
    categories: [] as TimerCategory[]
  });
  
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'intro' | 'category'>('intro'); // intro = imagen SVG, category = timer
  const [timerState, setTimerState] = useState<'ready' | 'running'>('ready'); // ready = timer visible pero no iniciado, running = timer corriendo
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  // Referencias para mantener valores actuales sin causar re-renders
  const configRef = useRef(config);
  const viewModeRef = useRef(viewMode);
  const currentIndexRef = useRef(currentIndex);
  const timerStateRef = useRef(timerState);

  // Actualizar referencias cuando cambien los estados
  useEffect(() => { configRef.current = config; }, [config]);
  useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { timerStateRef.current = timerState; }, [timerState]);

  // Cargar configuración inicial solo una vez
  useEffect(() => {
    const savedConfig = localStorageManager.getConfig();
    if (savedConfig.timer) {
      setConfig({
        minutes: savedConfig.timer.minutes,
        seconds: savedConfig.timer.seconds,
        title: savedConfig.timer.title,
        categories: savedConfig.timer.categories || []
      });
      const initialTime = savedConfig.timer.minutes * 60 + savedConfig.timer.seconds;
      setTimeLeft(initialTime);
      setTotalTime(initialTime);
      console.log('⏱️ Configuración inicial cargada:', savedConfig.timer);
    }
  }, []); // Solo ejecutar una vez al montar

  // Event listeners separados para evitar recreaciones
  useEffect(() => {
    // Escuchar actualizaciones de configuración
    const handleConfigUpdate = (data: any) => {
      console.log('⏱️ TimerPage - Configuración recibida:', data);
      if (data.section === 'timer') {
        console.log('⏱️ Actualizando configuración del timer:', data.data);
        setConfig({
          minutes: data.data.minutes,
          seconds: data.data.seconds,
          title: data.data.title,
          categories: data.data.categories || []
        });
        // Actualizar tiempo solo si no está corriendo
        setTimeLeft(prev => {
          const newTime = data.data.minutes * 60 + data.data.seconds;
          setTotalTime(newTime);
          setIsFinished(false);
          console.log(`⏱️ Tiempo configurado a: ${newTime} segundos`);
          return newTime;
        });
      }
    };

    // Escuchar comandos del timer
    const handleTimerCommand = (command: any) => {
      console.log('⏱️ TimerPage - Comando recibido:', command);
      const { command: cmd, payload } = command;
      
      switch (cmd) {
        case 'start':
          console.log('▶️ Comando START recibido - Verificando estado actual');
          console.log('🔄 Estado actual:', { 
            viewMode: viewModeRef.current, 
            timerState: timerStateRef.current, 
            isRunning 
          });
          
          // Solo iniciar si estamos en modo categoría y en estado ready
          if (viewModeRef.current === 'category' && timerStateRef.current === 'ready') {
            setTimeLeft(currentTime => {
              if (currentTime > 0) {
                console.log('▶️ Iniciando timer desde comando START...');
                setTimerState('running');
                setIsRunning(true);
                setIsFinished(false);
              } else {
                console.log('⚠️ No se puede iniciar el timer: tiempo = 0');
              }
              return currentTime;
            });
          } else {
            console.log('⚠️ Ignorando comando START - Estado no válido para iniciar');
            console.log(`⚠️ ViewMode: ${viewModeRef.current}, TimerState: ${timerStateRef.current}`);
          }
          break;
        case 'pause':
          console.log('⏸️ Pausando timer...');
          setIsRunning(false);
          break;
        case 'reset':
          console.log('⏹️ Reiniciando timer...');
          setIsRunning(false);
          setIsFinished(false);
          setConfig(currentConfig => {
            const resetTime = currentConfig.minutes * 60 + currentConfig.seconds;
            setTimeLeft(resetTime);
            setTotalTime(resetTime);
            console.log(`⏱️ Timer reiniciado a: ${resetTime} segundos`);
            return currentConfig;
          });
          break;
        case 'setTime':
          if (payload && typeof payload.minutes === 'number' && typeof payload.seconds === 'number') {
            const newTime = payload.minutes * 60 + payload.seconds;
            setTimeLeft(newTime);
            setTotalTime(newTime);
            setIsRunning(false);
            setIsFinished(false);
            console.log(`⏱️ Tiempo establecido a: ${newTime} segundos`);
          }
          break;
        case 'next':
          console.log('➡️ Comando NEXT recibido');
          console.log('🔄 Estado antes del cambio:', { 
            viewMode: viewModeRef.current, 
            currentIndex: currentIndexRef.current, 
            timerState: timerStateRef.current, 
            totalCategories: configRef.current.categories.length 
          });
          
          if (viewModeRef.current === 'intro') {
            // De imagen a primera categoría disponible (timer listo pero no iniciado)
            if (configRef.current.categories && configRef.current.categories.length > 0) {
              console.log('📱 Cambiando a categoría (timer listo):', currentIndexRef.current);
              setViewMode('category');
              setTimerState('ready');
              // Cargar timer directamente aquí
              if (configRef.current.categories[currentIndexRef.current]) {
                const category = configRef.current.categories[currentIndexRef.current];
                const newTime = category.minutes * 60 + category.seconds;
                setTimeLeft(newTime);
                setTotalTime(newTime);
                setIsRunning(false);
                setIsFinished(false);
                console.log(`⏱️ Cargada categoría: ${category.name}, tiempo: ${newTime}s (listo para iniciar)`);
              }
            } else {
              console.log('⚠️ No hay categorías disponibles');
            }
          } else if (viewModeRef.current === 'category' && timerStateRef.current === 'ready') {
            // De timer listo a timer iniciado
            console.log('▶️ Iniciando timer desde estado listo');
            setTimerState('running');
            setIsRunning(true);
            setIsFinished(false);
          } else if (viewModeRef.current === 'category' && timerStateRef.current === 'running') {
            // De timer corriendo a imagen intermedia
            console.log('🖼️ Cambiando a imagen intermedia desde timer corriendo');
            setViewMode('intro');
            setTimerState('ready');
            setIsRunning(false);
            setIsFinished(false);
            
            // Preparar el siguiente índice
            const nextIndex = currentIndexRef.current + 1;
            if (nextIndex < configRef.current.categories.length) {
              console.log('📊 Estableciendo siguiente índice:', nextIndex);
              setCurrentIndex(nextIndex);
            } else {
              console.log('🔄 Volviendo al inicio (índice 0)');
              setCurrentIndex(0);
            }
          }
          break;
        case 'prev':
          console.log('⬅️ Comando PREV recibido');
          console.log('🔄 Estado antes del cambio:', { 
            viewMode: viewModeRef.current, 
            currentIndex: currentIndexRef.current, 
            timerState: timerStateRef.current, 
            totalCategories: configRef.current.categories.length 
          });
          
          if (viewModeRef.current === 'intro') {
            // De imagen a categoría (estado listo)
            if (configRef.current.categories && configRef.current.categories.length > 0) {
              const targetIndex = currentIndexRef.current === 0 ? configRef.current.categories.length - 1 : currentIndexRef.current - 1;
              console.log('📱 Cambiando a categoría (estado listo):', targetIndex);
              setCurrentIndex(targetIndex);
              setViewMode('category');
              setTimerState('ready');
              // Cargar timer directamente aquí
              if (configRef.current.categories[targetIndex]) {
                const category = configRef.current.categories[targetIndex];
                const newTime = category.minutes * 60 + category.seconds;
                setTimeLeft(newTime);
                setTotalTime(newTime);
                setIsRunning(false);
                setIsFinished(false);
                console.log(`⏱️ Cargada categoría: ${category.name}, tiempo: ${newTime}s (listo para iniciar)`);
              }
            }
          } else if (viewModeRef.current === 'category' && timerStateRef.current === 'ready') {
            // De timer listo a imagen
            console.log('🖼️ Cambiando a imagen desde timer listo');
            setViewMode('intro');
            setTimerState('ready');
          } else if (viewModeRef.current === 'category' && timerStateRef.current === 'running') {
            // De timer corriendo a timer listo (pausar)
            console.log('⏸️ Pausando timer (volviendo a estado listo)');
            setTimerState('ready');
            setIsRunning(false);
          }
          break;
        case 'goToCategory':
          if (payload && typeof payload.index === 'number') {
            console.log(`🎯 Comando GO TO CATEGORY recibido, índice: ${payload.index}`);
            const index = payload.index;
            if (index >= 0 && index < configRef.current.categories.length) {
              setCurrentIndex(index);
              setViewMode('category');
              setTimerState('ready');
              // Cargar timer directamente aquí
              if (configRef.current.categories[index]) {
                const category = configRef.current.categories[index];
                const newTime = category.minutes * 60 + category.seconds;
                setTimeLeft(newTime);
                setTotalTime(newTime);
                setIsRunning(false);
                setIsFinished(false);
                console.log(`⏱️ Cargada categoría: ${category.name}, tiempo: ${newTime}s (listo para iniciar)`);
              }
            }
          }
          break;
        default:
          console.log('❓ Comando no reconocido:', cmd);
      }
    };

    socketService.on('configUpdate', handleConfigUpdate);
    socketService.on('timerCommand', handleTimerCommand);

    return () => {
      socketService.off('configUpdate', handleConfigUpdate);
      socketService.off('timerCommand', handleTimerCommand);
    };
    // Los handlers leen de refs (configRef, viewModeRef, ...) para ver valores
    // frescos sin re-registrar listeners en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Solo recreamos el interval cuando cambia `isRunning`. Antes no había array
  // de deps y el efecto se reejecutaba en cada render, creando y destruyendo
  // el interval cada render — el timer contaba de forma errática.
  useEffect(() => {
    if (!isRunning) return;

    const interval = setInterval(() => {
      setTimeLeft(time => {
        const newTime = time - 1;
        if (newTime <= 0) {
          setIsRunning(false);
          setIsFinished(true);
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning]);

  // useEffect para monitorear cambios de estado
  useEffect(() => {
    console.log('🔄 Estado cambiado - ViewMode:', viewMode, 'CurrentIndex:', currentIndex, 'TimerState:', timerState);
  }, [viewMode, currentIndex, timerState]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (isFinished) return 'timer-finished';
    if (timeLeft <= 30 && timeLeft > 0) return 'timer-warning';
    if (timeLeft <= 10 && timeLeft > 0) return 'timer-critical';
    return '';
  };

  const getCurrentCategoryName = () => {
    if (viewMode === 'category' && config.categories[currentIndex]) {
      return config.categories[currentIndex].name;
    }
    return config.title;
  };

  return (
    <div className={`timer-page ${getTimerClass()}`}>
      <SocketNotification showOnMount={true} />
      
        {viewMode === 'intro' ? (
        // Vista de imagen SVG a pantalla completa
        <div className="intro-container">
          <img src="/img/neurolimpiadas_back.svg" alt="Neurolimpiadas" className="intro-image" />
        </div>
      ) : (
        // Vista del timer con categoría
        <div className="timer-container">
          <div className="timer-header">
            <h1 className="timer-title">{getCurrentCategoryName()}</h1>
          </div>
          
          <div className="timer-display">
            <div className="time-value">{formatTime(timeLeft)}</div>
            {isFinished && (
              <div className="finished-message">¡TIEMPO TERMINADO!</div>
            )}
          </div>

          <div className="timer-progress" style={{ display: 'none' }}>
            <div 
              className="progress-bar"
              style={{
                width: totalTime > 0 ? `${((totalTime - timeLeft) / totalTime) * 100}%` : '0%'
              }}
            ></div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default TimerPage;
